
import React, { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import { useGameStateManager } from './game/GameStateManager';
import { GameBoardView } from './game/GameBoardView';
import { getMaxAttemptsForLevel, calculateLevelFromWordLength } from './game/GameLogic';
import { saveGameState } from './game/GamePersistenceV2';
import { useIsMobile } from '@/hooks/use-mobile';
import { toast } from 'sonner';
import { handleGameWinFinal, handleGameLoss, initializeGameStatus, shouldShowCompletion } from './game/managers/GameStatusManager';
import GameCompletionScreen from './GameCompletionScreen';
import { useGameResetListener } from '@/lib/gameReset';

const GameBoard = ({
  currentWord,
  attempts,
  maxAttempts,
  onCorrectGuess,
  onGameOver,
  onAttempt,
  registerKeyboardHandlers,
  immediateCompletion = false,
  compactLayout = false,
}) => {
  const isMobile = useIsMobile();
  // Create a reference to track if we've already saved state
  const hasSavedStateRef = useRef(false);
  const initialRenderRef = useRef(true);
  const [forceRefresh, setForceRefresh] = useState(0);

  // Guard check: avoid persistence during mode switching (Android-specific race fix)
  const isModeSwitchInProgress = () => localStorage.getItem('babblegram_mode_switch_in_progress') === '1';

  const currentLevel = useMemo(() => 
    currentWord ? calculateLevelFromWordLength(currentWord.length) : 1,
    [currentWord]
  );

  const {
    guesses,
    currentGuess,
    setCurrentGuess,
    keyboardStatus,
    isValidating,
    previousWord,
    isTransitioning,
    showGameCompletion,
    setShowGameCompletion,
    gameStatus,
    setGameStatus,
    guessHistory,
    invalidGuessType,
    levelAttempts,
    isLoading,
    handleSubmitGuess,
    resetGameState
  } = useGameStateManager(currentWord, async () => {
    // Ensure streak is updated before proceeding
    if (currentLevel === 4) {
      await handleGameWinFinal(setGameStatus, setShowGameCompletion, currentLevel, true);
    }
    onCorrectGuess();
  }, async () => {
    // Set the game status to lost but don't show completion screen yet
    await handleGameLoss(setGameStatus, () => {}, currentLevel);
    // The completion screen will be shown after the animation completes
    // via the onAnimationComplete callback in WordRow
    onGameOver();
  }, onAttempt);

  // Listen for game reset events
  useGameResetListener(() => {
    console.log('GameBoard received game reset event');
    if (resetGameState) {
      resetGameState();
      // Force a re-render
      setForceRefresh(prev => prev + 1);
    }
  });

  // Initialize game status when component mounts
  useEffect(() => {
    const initGameStatus = async () => {
      const initialState = await initializeGameStatus();
      if (initialState.showCompletion) {
        setShowGameCompletion(true);
      }
    };
    initGameStatus();
  }, [setShowGameCompletion]);

  // Helper function to handle key presses
  const handleKeyPress = useCallback((key: string) => {
    if (key === "Backspace") {
      setCurrentGuess((prev: string) => prev.slice(0, -1));
    } else {
      setCurrentGuess((prev: string) =>
        prev.length < currentWord.length ? prev + key.toLowerCase() : prev
      );
    }
  }, [currentWord, setCurrentGuess]);

  // Create a stable ref for handleSubmitGuess to prevent infinite loops
  const handleSubmitGuessRef = useRef(handleSubmitGuess);
  handleSubmitGuessRef.current = handleSubmitGuess;

  // Handler for submitting guesses
  const handleSubmitGuessCallback = useCallback(() => {
    handleSubmitGuessRef.current();
  }, []); // Now this is stable

  // Register keyboard handlers with the parent component
  // Fix: Use a more stable reference that won't change on every render
  const stableHandlers = useMemo(() => ({
    onKeyPress: handleKeyPress,
    onSubmit: handleSubmitGuessCallback,
    keyboardStatus,
    currentGuess
  }), [handleKeyPress, handleSubmitGuessCallback, keyboardStatus, currentGuess]);

  useEffect(() => {
    if (currentWord) {
      registerKeyboardHandlers(stableHandlers);
    }
  }, [currentWord, stableHandlers]); // Remove registerKeyboardHandlers from deps

  // Debug log to check guesses initially loaded
  useEffect(() => {
    if (guesses && guesses.length > 0) {
      console.log('GameBoard has loaded guesses:', { 
        guessCount: guesses.length, 
        guessDetails: guesses.map(g => g.map(l => l.letter).join('')),
        currentWord,
        currentLevel,
        timestamp: new Date().toISOString()
      });
      
      // Set initialRender to false once we have guesses
      initialRenderRef.current = false;
    }
  }, [guesses, currentWord, currentLevel]);

  // Clear mode-switch guard when the game board becomes stable in the new mode
  useEffect(() => {
    if (!currentWord) return;
    if (!isLoading && !isTransitioning) {
      setTimeout(() => {
        try {
          localStorage.removeItem('babblegram_mode_switch_in_progress');
          localStorage.removeItem('babblegram_mode_switch_target_mode');
        } catch {}
      }, 200);
    }
  }, [currentWord, isLoading, isTransitioning]);

  // Also clear the guard when we receive explicit mode-switched events
  useEffect(() => {
    const clearGuard = () => {
      try {
        localStorage.removeItem('babblegram_mode_switch_in_progress');
        localStorage.removeItem('babblegram_mode_switch_target_mode');
      } catch {}
    };
    window.addEventListener('mode-switched', clearGuard as any);
    window.addEventListener('babblegram_mode_switched', clearGuard as any);
    return () => {
      window.removeEventListener('mode-switched', clearGuard as any);
      window.removeEventListener('babblegram_mode_switched', clearGuard as any);
    };
  }, []);

  // Game state persistence - with debounce to avoid circular updates
  useEffect(() => {
    // Skip initial rendering and don't persist during transitions or mode switching
    if (!currentWord || isTransitioning || isLoading || isModeSwitchInProgress()) {
      return;
    }

    // Only persist state when something relevant has changed and 
    // avoid setting state inside the effect that would trigger re-renders
    const persistState = async () => {
      try {
        const currentLevelHistory = guessHistory?.find(h => h.level === currentLevel);
        const currentLevelGuesses = currentLevelHistory?.guesses || [];

        // Debug log but prevent needless updates by using ref
        if (!hasSavedStateRef.current) {
          console.log('Persisting initial game state:', {
            word: currentWord,
            isTransitioning,
            hasPreviousWord: !!previousWord,
            level: currentLevel,
            guessCount: currentLevelGuesses.length,
            timestamp: new Date().toISOString()
          });
          
          hasSavedStateRef.current = true; // Mark that we've saved state at least once
        }
        
        await saveGameState(
          guessHistory || [],
          levelAttempts || [],
          gameStatus,
          currentWord,
          currentLevel,
          guesses, // Critical fix: Use current guesses from state
          guesses.length, // Use current guesses length
          keyboardStatus,
          previousWord,
          isTransitioning
        );
      } catch (error) {
        console.error('Error saving game state:', error);
        toast.error("There was an error saving your progress.");
      }
    };

    // Use a timeout to debounce the persistence but only create it once
    const timeoutId = setTimeout(() => {
      persistState();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [
    // Include ALL dependencies that should trigger state persistence
    currentWord,
    gameStatus,
    guesses, // Critical fix: Add guesses as a dependency
    previousWord?.word, 
    guessHistory?.length,
    levelAttempts?.length,
    isTransitioning,
    isLoading,
    keyboardStatus,
    currentLevel
  ]);

  return (
    <div>
      {/* Render Game Board */}
      <GameBoardView
        currentWord={currentWord}
        guesses={guesses}
        currentGuess={currentGuess}
        keyboardStatus={keyboardStatus}
        isValidating={isValidating}
        previousWord={previousWord}
        isTransitioning={isTransitioning}
        showGameCompletion={showGameCompletion}
        setShowGameCompletion={setShowGameCompletion}
        gameStatus={gameStatus}
        guessHistory={guessHistory}
        invalidGuessType={invalidGuessType}
        levelAttempts={levelAttempts}
        handleSubmitGuess={handleSubmitGuess}
        immediateCompletion={immediateCompletion}
        compactLayout={compactLayout}
      />
      {/* Render Game Completion Screen if necessary */}
      {/* Completion screen rendered centrally in GameBoardView */}
    </div>
  );
};

export default GameBoard;
